import { useInfiniteQuery } from 'react-query';
import type { QueryOptions } from 'react-query';
import { AxiosError } from 'axios';
import { searchClient } from 'apis/algolia';

import type { IEmployeeWallet } from 'hooks/useQueryProfile';

import {
  getProductsQueryParams,
  groupAndCountResultsForMultipleWallets,
} from 'utils/algolia';
import type { Product } from 'utils/algolia';
import * as Amplitude from 'utils/amplitude';

type CommonArgs = {
  a_b_test_id: 'enter' | 'keystroke'; // this is a temporary hack
  country: string;
  employee_wallets: IEmployeeWallet[];
  query: string;
};

const PRODUCT_LIMIT_PER_PAGE = 9;
const ALGOLIA_VENDOR_LIST_INDEX = 'vendors_list_v1';

const getSearchProducts = async ({
  a_b_test_id,
  country,
  employee_wallets,
  page,
  query,
}: {
  page: number;
} & CommonArgs) => {
  const search_products_params = employee_wallets.map((employee_wallet) =>
    getProductsQueryParams({
      employee_wallet,
      country,
    })
  );
  const search_products_queries = search_products_params.map((params) => ({
    indexName: ALGOLIA_VENDOR_LIST_INDEX,
    query,
    params: {
      page,
      hitsPerPage: PRODUCT_LIMIT_PER_PAGE,
      facetFilters: params.facet_filters,
      numericFilters: params.numeric_filters,
    },
  }));
  const { results } = await searchClient.multipleQueries<Product>(
    search_products_queries
  );
  if (page === 0) {
    Amplitude.logEvent(Amplitude.EVENTS.QuerySearched, {
      search_query: query,
      a_b_test_id,
    });
  }
  const { items, count, pages } =
    groupAndCountResultsForMultipleWallets(results);
  return {
    max_pages: pages,
    products: items,
    total_products: count,
  };
};

const getNextPageParam: QueryOptions<
  Awaited<ReturnType<typeof getSearchProducts>>
>['getNextPageParam'] = (last_page, pages) =>
  pages.length !== 0 && pages.length < last_page.max_pages
    ? pages.length
    : undefined;

const useInfiniteQuerySearchProducts = ({
  a_b_test_id,
  country,
  employee_wallets,
  onError,
  query,
}: {
  onError: (error: AxiosError) => void;
} & CommonArgs) => {
  const {
    data,
    fetchNextPage,
    hasNextPage: has_next_page,
    isFetchingNextPage: is_fetching_next_page,
    isLoading: is_loading,
  } = useInfiniteQuery(
    [`searchProducts`, { query, country }],
    ({ pageParam: page = 0 }) =>
      getSearchProducts({
        a_b_test_id,
        country,
        employee_wallets,
        page,
        query,
      }),
    { getNextPageParam, onError }
  );

  return {
    data,
    is_loading,
    is_fetching_next_page,
    fetchNextPage,
    has_next_page,
  };
};

export default useInfiniteQuerySearchProducts;
